import React, { useState, useEffect, useCallback } from "react";
import {
  testimonial1,
  testimonial2,
  testimonial3,
  testimonial4,
  testimonial5,
  testimonial6,
  testimonial7,
  testimonial8,
  testimonial9,
  testimonial10,
} from "../../assets/data/Imagedata";
import { FaChevronLeft, FaChevronRight, FaHeart, FaShare, FaQuoteLeft } from "react-icons/fa";

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const images = [
    { url: testimonial1, link: "/gratitude" },
    { url: testimonial2, link: "/gratitude" },
    { url: testimonial3, link: "/gratitude" },
    { url: testimonial4, link: "/gratitude" },
    { url: testimonial5, link: "/gratitude" },
    { url: testimonial6, link: "/gratitude" },
    { url: testimonial7, link: "/gratitude" },
    { url: testimonial8, link: "/gratitude" },
    { url: testimonial9, link: "/gratitude" },
    { url: testimonial10, link: "/gratitude" },
  ];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  }, [images.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Auto-play interval effect
  useEffect(() => {
    let interval;
    if (!isHovered && !isFullscreen) {
      interval = setInterval(() => {
        nextSlide();
      }, 4000);
    }
    
    return () => clearInterval(interval);
  }, [isHovered, currentIndex, nextSlide, isFullscreen]);

  // Handle keyboard navigation for fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isFullscreen) {
        if (e.key === 'Escape') {
          setIsFullscreen(false);
        } else if (e.key === 'ArrowRight') {
          nextSlide();
        } else if (e.key === 'ArrowLeft') {
          prevSlide();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, nextSlide, prevSlide]);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-96 h-96 bg-blue-100 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-100 rounded-full opacity-10 blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full"></div>
            <span className="text-sm font-semibold tracking-wider text-blue-600 uppercase">
              Consumer Testimonials
            </span>
            <div className="w-12 h-1 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              What Our Consumers Say
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Real stories from consumers across India who have experienced our commitment 
            to protecting consumer rights and ensuring fair treatment.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-blue-100">
            <div className="text-2xl font-bold text-blue-600">500+</div>
            <div className="text-gray-700 text-sm">Testimonials</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-green-100">
            <div className="text-2xl font-bold text-green-600">98%</div>
            <div className="text-gray-700 text-sm">Satisfaction</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-orange-100">
            <div className="text-2xl font-bold text-orange-600">12K+</div>
            <div className="text-gray-700 text-sm">Helped</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-purple-100">
            <div className="text-2xl font-bold text-purple-600">24/7</div>
            <div className="text-gray-700 text-sm">Support</div>
          </div>
        </div>

        {/* Main Carousel - Single Image Display */}
        <div 
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Decorative Quote Icon */}
          <div className="absolute -top-6 -left-6 z-10 hidden md:block">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-2xl">
              <FaQuoteLeft className="text-3xl text-white" />
            </div>
          </div>

          {/* Carousel Container */}
          <div className="relative bg-white rounded-3xl shadow-2xl border border-blue-100 overflow-hidden">
            {/* Single Current Image Display */}
            <div className="relative h-[500px] md:h-[600px] overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
                <img
                  src={images[currentIndex].url}
                  alt={`Consumer Testimonial ${currentIndex + 1}`}
                  className="max-w-full max-h-full object-contain p-8 cursor-pointer hover:opacity-95 transition-opacity duration-300"
                  onClick={handleImageClick}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Image Counter */}
              <div className="absolute top-6 left-6 z-20">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
                  <span className="text-lg font-bold text-blue-600">
                    {currentIndex + 1} / {images.length}
                  </span>
                </div>
              </div>

              {/* Social Actions */}
              <div className="absolute top-6 right-6 z-20 flex gap-3">
                <button 
                  onClick={() => window.open(images[currentIndex].url, '_blank')}
                  className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg hover:bg-white transition-colors duration-300"
                  aria-label="Share testimonial"
                >
                  <FaShare className="text-blue-600" />
                </button>
              </div>

              {/* Navigation Dots - Minimal */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20">
                <div className="flex space-x-3">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentIndex 
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600 scale-125 shadow-lg" 
                          : "bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Buttons - Only show on hover */}
            <button
              onClick={prevSlide}
              className={`absolute top-1/2 left-4 transform -translate-y-1/2 z-20 w-12 h-12 bg-white text-blue-600 rounded-full shadow-xl hover:shadow-2xl hover:bg-blue-50 transition-all duration-300 flex items-center justify-center border border-blue-200 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              aria-label="Previous testimonial"
            >
              <FaChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={nextSlide}
              className={`absolute top-1/2 right-4 transform -translate-y-1/2 z-20 w-12 h-12 bg-white text-blue-600 rounded-full shadow-xl hover:shadow-2xl hover:bg-blue-50 transition-all duration-300 flex items-center justify-center border border-blue-200 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
              aria-label="Next testimonial"
            >
              <FaChevronRight className="w-5 h-5" />
            </button>
          </div>

         

          {/* View Gallery Button */}
          <div className="mt-8 text-center">
            <a
              href="/gratitude"
              className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
            >
              View All Testimonials
              <FaChevronRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>
        </div>

        {/* Fullscreen Modal */}
        {isFullscreen && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-6 right-6 text-white text-3xl z-50 hover:text-gray-300 transition-colors"
              aria-label="Close fullscreen"
            >
              ✕
            </button>
            
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={images[currentIndex].url}
                alt="Fullscreen testimonial"
                className="max-w-full max-h-full object-contain"
              />
              
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-4">
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  aria-label="Previous"
                >
                  <FaChevronLeft />
                </button>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
                  {currentIndex + 1} / {images.length}
                </div>
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                  aria-label="Next"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;